import React from 'react';
import { useTheme } from '../../context/ThemeContext';
import toast from 'react-hot-toast';

/**
 * Theme Selector Component
 * Displays preview cards for each available theme
 */
const ThemeSelector = () => {
  const { theme, setTheme, themes } = useTheme();

  const handleThemeChange = async (themeName) => {
    try {
      await setTheme(themeName);
      toast.success(`Theme changed to ${themes[themeName].name}`);
    } catch (error) {
      console.error('Failed to change theme:', error);
      toast.error('Failed to change theme');
    }
  };

  const themeList = [
    { id: 'aurora', icon: '🌌' },
    { id: 'forest', icon: '🌲' },
    { id: 'ocean', icon: '🌊' },
    { id: 'sunset', icon: '🌅' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Theme Preferences</h3>
        <p className="text-xs text-gray-500">
          🎨 Choose your preferred color theme
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {themeList.map(({ id, icon }) => {
          const themeData = themes[id];
          const isSelected = theme === id;
          
          return (
            <button
              key={id}
              onClick={() => handleThemeChange(id)}
              className="relative p-4 rounded-xl border-2 transition-all duration-200"
              style={{
                borderColor: isSelected ? themeData.primary : '#E5E7EB',
                backgroundColor: isSelected ? themeData.primary50 : '#FFFFFF',
                boxShadow: isSelected ? '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' : 'none',
                transform: isSelected ? 'scale(1.05)' : 'scale(1)',
              }}
              onMouseEnter={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = '#D1D5DB';
                  e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = '#E5E7EB';
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
            >
              {/* Selected indicator */}
              {isSelected && (
                <div className="absolute top-2 right-2">
                  <div 
                    className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                    style={{ backgroundColor: themeData.primary }}
                  >
                    ✓
                  </div>
                </div>
              )}
              
              {/* Theme preview */}
              <div className="flex items-start space-x-4">
                {/* Color swatches */}
                <div className="flex flex-col space-y-2">
                  <div 
                    className="w-16 h-12 rounded-lg shadow-sm"
                    style={{ 
                      background: `linear-gradient(135deg, ${themeData.primary} 0%, ${themeData.secondary} 100%)` 
                    }}
                  />
                  <div className="flex space-x-1">
                    <div 
                      className="w-4 h-4 rounded-full border border-gray-200"
                      style={{ backgroundColor: themeData.primary }}
                    />
                    <div 
                      className="w-4 h-4 rounded-full border border-gray-200"
                      style={{ backgroundColor: themeData.secondary }}
                    />
                    <div 
                      className="w-4 h-4 rounded-full border border-gray-200"
                      style={{ backgroundColor: themeData.accent }}
                    />
                    {id === 'forest' && themeData.accentWarm && (
                      <div 
                        className="w-4 h-4 rounded-full border border-gray-200"
                        style={{ backgroundColor: themeData.accentWarm }}
                      />
                    )}
                  </div>
                </div>
                
                {/* Theme info */}
                <div className="flex-1 text-left">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="text-2xl">{icon}</span>
                    <h4 className="font-semibold text-gray-900">{themeData.name}</h4>
                  </div>
                  <p className="text-xs text-gray-500">{themeData.description}</p>
                  
                  {/* Sample button preview */}
                  <div className="mt-3">
                    <div 
                      className="px-3 py-1.5 rounded-lg text-xs font-medium text-white inline-block"
                      style={{ backgroundColor: themeData.primary }}
                    >
                      Sample Button
                    </div>
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ThemeSelector;
